import React, { useState } from "react"
import { X, Check, RotateCcw, Brain } from "lucide-react"

const FlashcardPopup = ({
	flashcard,
	handleCloseFlashcard,
	onMarkAsKnown,
	isKnown = false,
}) => {
	const [isFlipped, setIsFlipped] = useState(false)
	const [showConfirmation, setShowConfirmation] = useState(false)

	const handleMarkAsKnown = () => {
		if (onMarkAsKnown) {
			onMarkAsKnown(flashcard.id)
			setShowConfirmation(true)
			// Auto-close after showing confirmation
			setTimeout(() => {
				handleCloseFlashcard()
			}, 1500)
		}
	}

	const handleFlip = () => {
		setIsFlipped(!isFlipped)
	}

	const handleReset = () => {
		setIsFlipped(false)
	}

	if (showConfirmation) {
		return (
			<div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-20 p-4'>
				<div className='bg-[#1E1E1E] rounded-lg p-6 max-w-md w-full text-center'>
					<div className='text-6xl mb-4'>🎉</div>
					<h3 className='text-white font-bold text-lg mb-2'>Great job!</h3>
					<p className='text-[#A0A0A0] mb-4'>
						You marked "
						<span className='text-[#FF3A6E] font-semibold'>
							{flashcard.front}
						</span>
						" as known!
					</p>
					<div className='flex items-center justify-center text-green-400 text-sm'>
						<Check size={16} className='mr-1' />
						<span>Added to your known words</span>
					</div>
				</div>
			</div>
		)
	}

	return (
		<div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-20 p-4'>
			<div className='bg-[#1E1E1E] rounded-lg p-4 sm:p-6 max-w-md w-full'>
				<div className='flex justify-between items-center mb-4 sm:mb-6'>
					<div className='flex items-center'>
						<h3 className='text-white font-bold text-base sm:text-lg mr-2'>
							Flashcard
						</h3>
						{isKnown && (
							<div className='bg-green-600 text-white text-xs px-2 py-1 rounded-full flex items-center'>
								<Check size={12} className='mr-1' />
								Known
							</div>
						)}
					</div>
					<button
						className='text-[#A0A0A0] hover:text-white p-1 transition-colors'
						onClick={handleCloseFlashcard}
						aria-label='Close'>
						<X size={20} />
					</button>
				</div>

				{/* Flashcard */}
				<div
					className={`bg-[#2C2C2C] rounded-lg p-4 sm:p-8 mb-4 sm:mb-6 text-center cursor-pointer transition-all duration-300 hover:bg-[#3C3C3C] ${
						isFlipped ? "transform" : ""
					}`}
					onClick={handleFlip}>
					<div className='min-h-[100px] flex flex-col items-center justify-center'>
						{!isFlipped ? (
							<>
								<div className='text-xl sm:text-2xl font-bold mb-2 text-white'>
									{flashcard.front}
								</div>
								<div className='text-[#A0A0A0] text-sm'>
									Click to reveal answer
								</div>
							</>
						) : (
							<>
								<div className='text-[#FF3A6E] text-lg sm:text-xl font-semibold mb-2'>
									{flashcard.back}
								</div>
								<div className='text-[#A0A0A0] text-sm mb-3'>
									{flashcard.category}
								</div>
								{flashcard.example && (
									<div className='text-[#A0A0A0] text-sm italic border-t border-[#3C3C3C] pt-3 mt-3'>
										Example: {flashcard.example}
									</div>
								)}
							</>
						)}
					</div>
				</div>

				{/* Controls */}
				<div className='flex justify-between items-center mb-4'>
					<button
						className='bg-[#2C2C2C] text-white p-2 rounded-lg hover:bg-[#3C3C3C] transition-colors'
						onClick={handleReset}
						title='Reset card'>
						<RotateCcw size={16} />
					</button>

					<div className='text-[#A0A0A0] text-sm'>{flashcard.category}</div>
				</div>

				{/* Action Buttons */}
				<div className='flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2'>
					<button
						className='bg-[#2C2C2C] text-white py-2 px-4 rounded-lg hover:bg-[#3C3C3C] transition-colors order-2 sm:order-1'
						onClick={handleCloseFlashcard}>
						{isFlipped ? "Study More" : "Close"}
					</button>

					{isFlipped && !isKnown && (
						<>
							<button
								className='bg-orange-600 text-white py-2 px-4 rounded-lg hover:bg-orange-700 transition-colors order-1 sm:order-2 flex items-center justify-center'
								onClick={() => {
									// Reset and close for "Need Practice"
									setIsFlipped(false)
									handleCloseFlashcard()
								}}>
								<Brain size={16} className='mr-2' />
								Need Practice
							</button>

							<button
								className='bg-gradient-to-r from-[#FF3A6E] to-[#FF6D4B] text-white py-2 px-4 rounded-lg hover:from-[#ff2a5e] hover:to-[#ff5a3b] transition-all order-1 sm:order-3 flex items-center justify-center'
								onClick={handleMarkAsKnown}>
								<Check size={16} className='mr-2' />I Know This
							</button>
						</>
					)}

					{isKnown && (
						<button
							className='bg-green-600 text-white py-2 px-4 rounded-lg cursor-default order-1 sm:order-2 flex items-center justify-center'
							disabled>
							<Check size={16} className='mr-2' />
							Already Known
						</button>
					)}
				</div>

				{/* Progress hint */}
				{isFlipped && !isKnown && (
					<div className='mt-4 text-center text-[#A0A0A0] text-xs'>
						💡 Mark as "I Know This" to track your progress and focus on words
						you need to practice
					</div>
				)}
			</div>
		</div>
	)
}

export default FlashcardPopup
